<template>
    <div class="article-editor">
        <div class="editor-page-content">

            <!-- 主要编辑区域 -->
            <div class="main-editor-container">
                <!-- Markdown 编辑器 -->
                <div v-if="articleData.article_type === 'markdown'" class="editor-wrapper">
                    <MdEditor v-model="markdownContent" :preview="true" :toolbars="toolbars" :theme="editorTheme"
                        language="zh-CN" placeholder="在这里写下您的精彩文章..." @save="saveDraft"
                        :onUploadImg="handleUploadImg" />
                </div>

                <!-- 富文本编辑器 -->
                <div v-else-if="articleData.article_type === 'richtext'" class="editor-wrapper">
                    <RichTextEditor ref="richTextEditorRef" placeholder="在这里写下您的精彩文章..." @change="handleRichTextChange"
                        @load="handleRichTextEditorLoad" />
                </div>
            </div>

            <!-- 文章属性设置区域 -->
            <div class="article-settings">
                <a-card title="文章设置" :bordered="false">
                    <div class="settings-form">
                        <!-- 文章标题 -->
                        <div class="form-item">
                            <label class="form-label required">文章标题:</label>
                            <a-input v-model:value="articleData.title" placeholder="请输入文章标题..." :maxlength="100"
                                show-count size="large" />
                        </div>

                        <!-- 文章摘要 -->
                        <div class="form-item">
                            <label class="form-label">文章摘要:</label>
                            <a-textarea v-model:value="articleData.excerpt" placeholder="请输入文章摘要（留空将自动生成）..." :rows="3"
                                :maxlength="200" show-count />
                            <div class="field-hint">
                                如果不填写摘要，系统将自动截取正文前100字作为摘要
                            </div>
                        </div>

                        <!-- 封面图片 -->
                        <div class="form-item">
                            <label class="form-label">封面图片:</label>
                            <FileUpload v-model:value="articleData.cover_image_url" category="articleImages"
                                placeholder="请输入图片URL或点击上传封面图片" default-mode="url" @change="handleCoverChange" />
                        </div>

                        <!-- 分类 -->
                        <div class="form-item">
                            <label class="form-label">分类:</label>
                            <a-select v-model:value="articleData.category_id" placeholder="请选择分类" allow-clear
                                size="large" style="width: 100%">
                                <a-select-option v-for="category in categories" :key="category.id" :value="category.id">
                                    {{ category.name }}
                                </a-select-option>
                                <template #dropdownRender="{ menuNode: menu }">
                                    <div>
                                        <component :is="menu" />
                                        <a-divider style="margin: 4px 0;" />
                                        <div style="padding: 4px 8px; cursor: pointer;"
                                            @mousedown.prevent="showAddCategoryModal">
                                            <PlusOutlined style="margin-right: 8px; vertical-align: middle;" />
                                            <span style="vertical-align: middle;">添加新分类</span>
                                        </div>
                                    </div>
                                </template>
                            </a-select>
                        </div>

                        <!-- 标签 -->
                        <div class="form-item">
                            <label class="form-label">标签:</label>
                            <div class="tags-section">
                                <!-- 已选标签显示 -->
                                <div v-if="selectedTags.length > 0" class="selected-tags">
                                    <a-tag v-for="tag in selectedTags" :key="tag.id" closable @close="removeTag(tag)"
                                        :color="tag.color">
                                        <TagOutlined style="vertical-align: middle;" />
                                        <span style="vertical-align: middle; margin-left: 4px;">{{ tag.name }}</span>
                                    </a-tag>
                                </div>

                                <!-- 标签搜索选择器 -->
                                <a-select v-model:value="selectedTagId" @change="addTag" placeholder="搜索或选择标签..."
                                    style="width: 100%; margin-bottom: 8px;" allow-clear show-search
                                    :filter-option="filterTagOption">
                                    <a-select-option v-for="tag in availableTags" :key="tag.id" :value="tag.id">
                                        {{ tag.name }} ({{ tag.usage_count || 0 }})
                                    </a-select-option>
                                    <template #dropdownRender="{ menuNode: menu }">
                                        <div>
                                            <component :is="menu" />
                                            <a-divider style="margin: 4px 0;" />
                                            <div style="padding: 4px 8px; cursor: pointer;"
                                                @mousedown.prevent="showQuickAddTag">
                                                <PlusOutlined style="margin-right: 8px; vertical-align: middle;" />
                                                <span style="vertical-align: middle;">添加新标签</span>
                                            </div>
                                        </div>
                                    </template>
                                </a-select>
                            </div>
                        </div>

                        <!-- 可见性设置 -->
                        <div class="form-item">
                            <label class="form-label">可见性设置:</label>
                            <a-radio-group v-model:value="articleData.isPublic" size="large">
                                <a-radio :value="true">
                                    <EyeOutlined style="vertical-align: middle;" />
                                    <span style="vertical-align: middle; margin-left: 4px;">公开</span>
                                </a-radio>
                                <a-radio :value="false">
                                    <EyeInvisibleOutlined style="vertical-align: middle;" />
                                    <span style="vertical-align: middle; margin-left: 4px;">私密</span>
                                </a-radio>
                            </a-radio-group>
                        </div>

                        <!-- 存储方式选择 -->
                        <div class="form-item">
                            <label class="form-label">存储方式:</label>
                            <a-radio-group v-model:value="storageMode" @change="handleStorageModeChange" size="large">
                                <a-radio value="database">
                                    <DatabaseOutlined style="vertical-align: middle;" />
                                    <span style="vertical-align: middle; margin-left: 4px;">数据库存储</span>
                                </a-radio>
                                <a-radio value="object_storage">
                                    <CloudOutlined style="vertical-align: middle;" />
                                    <span style="vertical-align: middle; margin-left: 4px;">对象存储</span>
                                </a-radio>
                            </a-radio-group>
                            <div class="field-hint">
                                数据库存储：内容直接保存在数据库中，访问速度快<br>
                                对象存储：内容保存为文件到对象存储，节省数据库空间
                            </div>
                        </div>
                    </div>
                </a-card>
            </div>

            <!-- 底部操作按钮 -->
            <div class="bottom-actions">
                <div class="action-buttons">
                    <a-button @click="goBack" class="back-button">
                        <ArrowLeftOutlined style="vertical-align: middle;" />
                        <span style="vertical-align: middle; margin-left: 4px;">返回文章列表</span>
                    </a-button>

                    <a-space size="large" class="right-buttons">
                        <!-- 编辑器类型选择器 -->
                        <a-select v-model:value="articleData.article_type" @change="handleArticleTypeChange"
                            style="width: 120px" size="large" placement="topLeft">
                            <a-select-option value="markdown">
                                <FileTextOutlined style="vertical-align: middle;" />
                                <span style="vertical-align: middle; margin-left: 4px;">Markdown</span>
                            </a-select-option>
                            <a-select-option value="richtext">
                                <EditOutlined style="vertical-align: middle;" />
                                <span style="vertical-align: middle; margin-left: 4px;">富文本</span>
                            </a-select-option>
                        </a-select>

                        <!-- 取消/保存草稿按钮 -->
                        <a-button v-if="showCancelButton" @click="cancelEdit" :loading="isCancelling" size="large">
                            <CloseOutlined style="vertical-align: middle;" />
                            <span style="vertical-align: middle; margin-left: 4px;">取消编辑</span>
                        </a-button>

                        <a-button v-if="showSaveDraftButton" @click="saveDraft" :loading="isSaving"
                            :disabled="!canSaveDraft" size="large">
                            <FileTextOutlined style="vertical-align: middle;" />
                            <span style="vertical-align: middle; margin-left: 4px;">{{ isSaving ? '保存中...' : '保存草稿'
                                }}</span>
                        </a-button>

                        <a-button type="primary" @click="publishArticle" :loading="isPublishing" :disabled="!canPublish"
                            size="large">
                            <EyeOutlined style="vertical-align: middle;" />
                            <span style="vertical-align: middle; margin-left: 4px;">{{ publishButtonText }}</span>
                        </a-button>
                    </a-space>
                </div>
            </div>
        </div>

        <!-- 创建分类模态框 -->
        <CreateCategoryModal :visible="showCreateCategoryModal" :existing-categories="categories"
            @cancel="showCreateCategoryModal = false" @success="handleCategoryCreated" />

        <!-- 创建标签模态框 -->
        <CreateTagModal :visible="showCreateTagModal" :existing-tags="tags" @cancel="showCreateTagModal = false"
            @success="handleTagCreated" />

        <!-- 草稿恢复模态框 -->
        <DraftRecoveryModal :visible="showRecoveryModal" :cache-data="recoveryData" :draft-type="draftType"
            @recover="handleRecoverDraft" @ignore="handleIgnoreDraft" />
    </div>
</template>

<script>
import { message } from 'ant-design-vue'
import {
    FileTextOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    TagOutlined,
    PlusOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    CloseOutlined,
    DatabaseOutlined,
    CloudOutlined
} from '@ant-design/icons-vue'
import { MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import RichTextEditor from '../../components/RichTextEditor.vue'
import FileUpload from '../../components/FileUpload.vue'
import supabaseArticleService from '../../services/supabaseArticleService.js'
import supabaseTagService from '../../services/supabaseTagService.js'
import supabaseCategoryService from '../../services/supabaseCategoryService.js'
import articleDataCacheService from '../../services/articleDataCacheService.js'
import supabase from '../../services/supabaseClient.js'
import fileUploadService from '../../services/fileUploadService.js'
import AdminPageHeader from '../../components/admin/AdminPageHeader.vue'
import CreateCategoryModal from '../../components/admin/CreateCategoryModal.vue'
import CreateTagModal from '../../components/admin/CreateTagModal.vue'
import DraftRecoveryModal from '../../components/admin/DraftRecoveryModal.vue'
import { useArticleDraftStore } from '../../stores/articleDraft.js'

export default {
    name: 'ArticleEditor',
    components: {
        MdEditor,
        RichTextEditor,
        FileUpload,
        FileTextOutlined,
        EyeOutlined,
        EyeInvisibleOutlined,
        TagOutlined,
        PlusOutlined,
        EditOutlined,
        ArrowLeftOutlined,
        CloseOutlined,
        DatabaseOutlined,
        CloudOutlined,
        CreateCategoryModal,
        CreateTagModal,
        DraftRecoveryModal
    },
    props: {
        id: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isEditing: false,
            articleData: {
                title: '',
                excerpt: '',
                cover_image_url: '',
                category_id: '',
                tag_ids: [],
                isPublic: true, // 默认公开
                status: 'draft',
                content_url: '', // 新增：可选的对象存储URL
                article_type: 'markdown' // 默认文章类型为markdown
            },
            markdownContent: '',
            richTextContent: '',
            categories: [],
            tags: [],
            selectedTags: [],
            selectedTagId: '',
            newTagName: '',
            isSaving: false,
            isPublishing: false,
            isLoading: false,
            editorTheme: 'light',
            toolbars: [
                'bold', 'underline', 'italic', 'strikeThrough',
                '-',
                'title', 'sub', 'sup', 'quote', 'unorderedList', 'orderedList', 'task',
                '-',
                'codeRow', 'code', 'link', 'image', 'table', 'mermaid', 'katex',
                '-',
                'revoke', 'next', 'save',
                '=',
                'pageFullscreen', 'fullscreen', 'preview', 'previewOnly', 'htmlPreview', 'catalog'
            ],
            showCreateCategoryModal: false,
            showCreateTagModal: false,
            isCancelling: false, // 新增：取消编辑按钮的加载状态
            showRecoveryModal: false, // 新增：草稿恢复模态框的显示状态
            recoveryData: null, // 新增：草稿恢复模态框的数据
            draftType: 'article', // 新增：草稿类型
            storageMode: 'database' // 新增：存储方式，默认数据库存储
        }
    },
    computed: {
        canSaveDraft() {
            // 草稿只需要标题不为空
            return this.articleData.title.trim()
        },
        canPublish() {
            // 发布需要：标题、分类，且内容存在（编辑器内容或对象存储URL任一）
            const hasMarkdownContent = this.markdownContent && this.markdownContent.trim()
            const hasRichTextContent = this.richTextContent && this.richTextContent.trim()
            const hasUrl = this.articleData.content_url && this.articleData.content_url.trim()
            const hasContent = hasMarkdownContent || hasRichTextContent || hasUrl
            return this.articleData.title.trim() && this.articleData.category_id && hasContent
        },
        availableTags() {
            // 确保tag_ids是数组
            const tagIds = Array.isArray(this.articleData.tag_ids) ? this.articleData.tag_ids : []
            return this.tags.filter(tag =>
                !tagIds.includes(tag.id)
            )
        },

        // 控制按钮显示的计算属性
        showSaveDraftButton() {
            // 新文章创建时 或 编辑草稿状态的文章时显示保存草稿按钮
            return !this.isEditing || (this.isEditing && this.articleData.status === 'draft')
        },

        showCancelButton() {
            // 只在编辑已发布文章时显示取消编辑按钮
            return this.isEditing && this.articleData.status === 'published'
        },

        publishButtonText() {
            if (this.isPublishing) {
                return this.isEditing ? '更新中...' : '发布中...'
            }

            if (!this.isEditing) {
                return this.articleData.isPublic ? '发布文章（公开）' : '发布文章（私密）'
            }

            // 编辑模式统一显示"发布文章"
            return '发布文章'
        }
    },
    async mounted() {
        // 初始化草稿缓存系统
        const draftStore = useArticleDraftStore()
        const draftType = this.id ? 'edit' : 'create'

        // 检查是否有可恢复的缓存
        const existingCache = draftStore.initDraft(draftType, this.id)

        console.log('缓存检查:', {
            draftType,
            articleId: this.id,
            existingCache,
            hasTitle: existingCache?.title,
            hasContent: existingCache?.content,
            cacheKeys: existingCache ? Object.keys(existingCache) : [],
            fullCache: JSON.stringify(existingCache, null, 2)
        })

        await this.loadInitialData()

        if (this.id) {
            this.isEditing = true
            await this.loadArticle()
        }

        // 如果有缓存，显示恢复提示（放宽检查条件）
        if (existingCache && (existingCache.title?.trim() || existingCache.content?.trim())) {
            console.log('显示缓存恢复提示')
            this.showRecoveryModal = true
            this.recoveryData = existingCache
            this.draftType = draftType
        } else {
            console.log('没有找到可恢复的缓存')
        }

        // 启动自动保存
        this.startAutoSave()
    },

    beforeUnmount() {
        // 组件销毁前清理定时器
        const draftStore = useArticleDraftStore()
        draftStore.stopAutoSave()
    },
    methods: {
        async loadInitialData() {
            try {
                this.isLoading = true
                // 并行加载分类和标签数据（使用缓存）
                const [categoriesData, tagsData] = await Promise.all([
                    articleDataCacheService.getCategories(),
                    articleDataCacheService.getTags()
                ])

                this.categories = categoriesData || []
                this.tags = tagsData || []

                console.log('加载的分类:', this.categories.length)
                console.log('加载的标签:', this.tags.length)
            } catch (error) {
                console.error('加载初始数据失败:', error)
                // 降级到直接调用服务
                try {
                    const [categoriesData, tagsData] = await Promise.all([
                        supabaseCategoryService.list(),
                        supabaseTagService.list()
                    ])
                    this.categories = categoriesData || []
                    this.tags = tagsData || []
                } catch (fallbackError) {
                    console.error('降级加载也失败:', fallbackError)
                    message.error('加载数据失败，请刷新重试')
                }
            } finally {
                this.isLoading = false
            }
        },

        async loadArticle() {
            try {
                // 使用管理端专用方法获取文章（包含内容）
                const article = await supabaseArticleService.getByIdForAdmin(parseInt(this.id))

                if (!article) {
                    message.error('文章不存在！')
                    this.$router.push('/admin/dashboard')
                    return
                }

                this.articleData = {
                    title: article.title,
                    excerpt: article.excerpt,
                    cover_image_url: article.cover_image_url,
                    category_id: article.category_id,
                    tag_ids: (article.tag_ids || []).map(id => parseInt(id)), // 确保都是数字
                    isPublic: article.status === 'published' && article.visibility === 'public',
                    status: article.status,
                    content_url: article.content_url || '',
                    article_type: article.article_type || 'markdown' // 默认为markdown
                }

                // 判断存储模式：如果content为空且有content_url，则为对象存储模式
                if (!article.content && article.content_url) {
                    this.storageMode = 'object_storage'
                    // 从对象存储URL获取内容
                    await this.loadContentFromUrl(article.content_url)
                } else {
                    this.storageMode = 'database'
                    // 根据文章类型设置对应编辑器的内容
                    if (this.articleData.article_type === 'richtext') {
                        this.richTextContent = article.content || ''
                        this.markdownContent = ''
                        // 立即设置富文本编辑器内容
                        this.$nextTick(() => {
                            if (this.$refs.richTextEditorRef && this.richTextContent) {
                                this.$refs.richTextEditorRef.setContent(this.richTextContent, 'text/lake')
                            }
                        })
                    } else {
                        this.markdownContent = article.content || ''
                        this.richTextContent = ''
                    }
                }

                // 更新选中的标签
                this.updateSelectedTags()

                console.log('📄 文章加载完成:', {
                    title: article.title,
                    contentLength: this.markdownContent.length,
                    status: article.status,
                    visibility: article.visibility
                })

            } catch (error) {
                console.error('加载文章失败:', error)
                message.error('加载文章失败：' + error.message)
                this.$router.push('/admin/dashboard')
            }
        },

        updateSelectedTags() {
            // 确保tag_ids是数组
            if (!Array.isArray(this.articleData.tag_ids)) {
                this.articleData.tag_ids = []
            }
            this.selectedTags = this.tags.filter(tag =>
                this.articleData.tag_ids.includes(tag.id)
            )
        },

        addTag() {
            if (this.selectedTagId && !this.articleData.tag_ids.includes(parseInt(this.selectedTagId))) {
                // 确保存储的是数字类型的ID
                this.articleData.tag_ids.push(parseInt(this.selectedTagId))
                this.updateSelectedTags()
                this.selectedTagId = ''
            }
        },

        removeTag(tag) {
            // 确保比较的是数字类型
            this.articleData.tag_ids = this.articleData.tag_ids.filter(id => id !== tag.id)
            this.updateSelectedTags()
        },

        handleNewTagInput(event) {
            if (event.key === 'Enter') {
                event.preventDefault()
                this.createNewTag()
            }
        },

        async createNewTag() {
            const name = this.newTagName.trim()
            if (!name) return

            try {
                // 检查标签是否已存在
                const existingTag = this.tags.find(tag => tag.name === name)
                if (existingTag) {
                    message.warning('标签已存在')
                    return
                }

                // 创建新标签（颜色由触发器生成）
                const newTag = await supabaseTagService.create({ name })

                // 重新加载标签列表（强制刷新缓存）
                this.tags = await articleDataCacheService.getTags(true)

                // 自动选中新标签
                this.articleData.tag_ids.push(newTag.id)
                this.updateSelectedTags()

                this.newTagName = ''
                message.success('标签创建成功！')

            } catch (error) {
                console.error('创建标签失败:', error)
                message.error('创建标签失败：' + error.message)
            }
        },

        // 处理文章类型切换
        handleArticleTypeChange() {
            // 当切换文章类型时，可以选择是否保留内容或清空
            if (this.articleData.article_type === 'richtext') {
                // 如果从markdown切换到富文本，可以尝试转换内容
                if (this.markdownContent) {
                    // 简单的markdown到HTML转换（可以后续增强）
                    this.richTextContent = this.markdownContent.replace(/\n/g, '<br>')
                }
                // 等待富文本编辑器加载完成后再设置内容
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (this.$refs.richTextEditorRef && this.richTextContent) {
                            // 优先使用Lake格式，如果失败会自动降级到HTML
                            this.$refs.richTextEditorRef.setContent(this.richTextContent, 'text/lake')
                        }
                    }, 100)
                })
            } else if (this.articleData.article_type === 'markdown') {
                // 如果从富文本切换到markdown，可以尝试转换内容
                if (this.richTextContent) {
                    // 简单的HTML到markdown转换（可以后续增强）
                    this.markdownContent = this.richTextContent.replace(/<br>/g, '\n').replace(/<[^>]*>/g, '')
                }
            }
        },

        // 处理封面图片变化
        handleCoverChange(newUrl) {
            console.log('封面图片已更新:', newUrl)
            // 图片URL已经通过v-model自动同步到articleData.cover_image_url
        },

        // 处理富文本内容变化
        handleRichTextChange(content) {
            // 更新富文本内容
            this.richTextContent = content

            // 可以在这里添加其他逻辑，比如自动保存等
            console.log('富文本内容已更改，长度:', content?.length || 0)
        },

        // 处理富文本编辑器加载完成
        handleRichTextEditorLoad() {
            if (this.articleData.article_type === 'richtext' && this.richTextContent) {
                // 使用Lake格式设置内容（保持原始格式）
                this.$refs.richTextEditorRef.setContent(this.richTextContent, 'text/lake')
            }
        },

        // 获取当前编辑器的内容
        async getCurrentContent() {
            if (this.articleData.article_type === 'richtext') {
                if (this.$refs.richTextEditorRef) {
                    try {
                        // 获取Lake格式的内容用于保存
                        const content = this.$refs.richTextEditorRef.getContent('lake')
                        console.log('📝 获取到的内容:', content?.substring(0, 500))
                        return content || ''
                    } catch (error) {
                        console.warn('获取内容失败:', error)
                        return this.richTextContent || ''
                    }
                }
                return this.richTextContent || ''
            } else {
                return this.markdownContent || ''
            }
        },

        async saveDraft() {
            if (!this.canSaveDraft) {
                message.warning('请至少填写文章标题')
                return
            }

            this.isSaving = true
            const hideLoading = message.loading('正在保存草稿...', 0)

            try {
                // 手动保存一次缓存（用于测试）
                const draftStore = useArticleDraftStore()
                const cacheData = await this.getArticleDataForCache()
                draftStore.saveDraft(cacheData)

                const currentContent = this.getCurrentContent()

                // 根据存储模式处理内容和URL
                let finalContent = ''
                let finalContentUrl = null

                if (this.storageMode === 'object_storage') {
                    // 对象存储模式：先上传内容到对象存储获取URL，数据库content设为null
                    if (currentContent.trim()) {
                        const fileName = `article-${this.id || 'new'}-${Date.now()}.${this.articleData.article_type === 'richtext' ? 'html' : 'md'}`
                        const file = new File([currentContent], fileName, {
                            type: this.articleData.article_type === 'richtext' ? 'text/html' : 'text/markdown'
                        })

                        const result = await fileUploadService.uploadFile(file, 'articleFiles')
                        finalContentUrl = result.url
                        finalContent = null // 数据库content设为null
                    } else {
                        // 如果没有内容，就使用已有的content_url
                        finalContentUrl = this.articleData.content_url || null
                        finalContent = null
                    }
                } else {
                    // 数据库模式：内容保存到数据库，content_url设为null
                    finalContent = currentContent
                    finalContentUrl = null
                }

                const articleData = {
                    title: this.articleData.title,
                    excerpt: this.articleData.excerpt || '',
                    content: finalContent,
                    cover_image_url: this.articleData.cover_image_url || '',
                    category_id: this.articleData.category_id ? parseInt(this.articleData.category_id) : null,
                    tag_ids: this.articleData.tag_ids.map(id => parseInt(id)),
                    status: 'draft',
                    visibility: this.articleData.isPublic ? 'public' : 'private',
                    article_type: this.articleData.article_type
                }

                // 只在对象存储模式且有URL时才添加content_url
                if (finalContentUrl) {
                    articleData.content_url = finalContentUrl
                }

                // 修复：在对象存储模式下，传递原始内容用于字数统计
                if (this.storageMode === 'object_storage' && currentContent) {
                    articleData.originalContent = currentContent
                }

                console.log('🔍 调试信息 - 存储模式:', this.storageMode)
                console.log('🔍 调试信息 - 当前内容长度:', currentContent?.length || 0)
                console.log('🔍 调试信息 - finalContent:', finalContent)
                console.log('🔍 调试信息 - finalContentUrl:', finalContentUrl)
                console.log('🔍 调试信息 - 传递给数据库的数据:', JSON.stringify(articleData, null, 2))

                const result = this.isEditing
                    ? await supabaseArticleService.update(parseInt(this.id), articleData)
                    : await supabaseArticleService.create(articleData)

                hideLoading()
                message.success('草稿保存成功！')

                // 清理缓存（创建新文章时保存草稿后清理）
                if (!this.isEditing) {
                    this.clearDraftCache()
                }

                if (!this.isEditing && result) {
                    this.isEditing = true
                    this.$router.replace(`/admin/article/${result.id}`)
                }
            } catch (error) {
                hideLoading()
                console.error('保存草稿失败:', error)
                message.error('保存草稿失败：' + error.message)
            } finally {
                this.isSaving = false
            }
        },

        async publishArticle() {
            if (!this.canPublish) {
                message.warning('请填写完整的文章信息')
                return
            }

            this.isPublishing = true
            let hideLoading = message.loading('正在发布文章...', 0)

            try {
                const currentContent = this.getCurrentContent()

                // 根据存储模式处理内容和URL
                let finalContent = ''
                let finalContentUrl = null

                if (this.storageMode === 'object_storage') {
                    // 对象存储模式：先上传内容到对象存储获取URL，数据库content设为null
                    if (currentContent.trim()) {
                        const fileName = `article-${this.id || 'new'}-${Date.now()}.${this.articleData.article_type === 'richtext' ? 'html' : 'md'}`
                        const file = new File([currentContent], fileName, {
                            type: this.articleData.article_type === 'richtext' ? 'text/html' : 'text/markdown'
                        })

                        const result = await fileUploadService.uploadFile(file, 'articleFiles')
                        finalContentUrl = result.url
                        finalContent = null // 数据库content设为null
                    } else {
                        // 如果没有内容，就使用已有的content_url
                        finalContentUrl = this.articleData.content_url || null
                        finalContent = null
                    }
                } else {
                    // 数据库模式：内容保存到数据库，content_url设为null
                    finalContent = currentContent
                    finalContentUrl = null
                }

                const articleData = {
                    title: this.articleData.title,
                    excerpt: this.articleData.excerpt || '',
                    content: finalContent,
                    cover_image_url: this.articleData.cover_image_url || '',
                    category_id: parseInt(this.articleData.category_id),
                    tag_ids: this.articleData.tag_ids.map(id => parseInt(id)),
                    status: 'published',
                    visibility: this.articleData.isPublic ? 'public' : 'private',
                    article_type: this.articleData.article_type
                }

                // 只在对象存储模式且有URL时才添加content_url
                if (finalContentUrl) {
                    articleData.content_url = finalContentUrl
                }

                // 修复：在对象存储模式下，传递原始内容用于字数统计
                if (this.storageMode === 'object_storage' && currentContent) {
                    articleData.originalContent = currentContent
                }

                const result = this.isEditing
                    ? await supabaseArticleService.update(parseInt(this.id), articleData)
                    : await supabaseArticleService.create(articleData)

                hideLoading()
                message.success(`文章${this.articleData.isPublic ? '公开' : '私密'}发布成功！`)

                // 清理缓存（发布文章后清理）
                this.clearDraftCache()

                // 可以选择跳转到文章详情页或文章列表
                if (result) {
                    this.$router.push(`/article/${result.id}`)
                }
            } catch (error) {
                hideLoading()
                console.error('发布失败:', error)
                message.error('发布失败：' + error.message)
            } finally {
                this.isPublishing = false
            }
        },

        goBack() {
            if (confirm('确定要离开吗？未保存的更改将丢失。')) {
                this.$router.push('/admin/dashboard')
            }
        },

        handleCoverError(event) {
            event.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop'
        },

        // 处理编辑器粘贴/拖拽图片上传 - 直接上传到图床
        async handleUploadImg(files, callback) {
            try {
                const urls = []
                for (const file of files) {
                    console.log('🔄 Markdown编辑器开始上传图片:', file.name, file.size)

                    // 直接上传到图床
                    const result = await fileUploadService.uploadFile(file, 'articleImages')
                    urls.push(result.url)

                    console.log('✅ Markdown编辑器图片上传成功:', result.url)
                }
                callback(urls)
            } catch (err) {
                console.error('❌ Markdown编辑器图片上传失败:', err)
                message.error('图片上传失败，请重试')
                callback([])
            }
        },

        // 返回文章列表
        goBack() {
            this.$router.push('/admin/dashboard')
        },

        // 显示添加分类模态框
        showAddCategoryModal() {
            this.showCreateCategoryModal = true
        },

        // 显示添加标签模态框
        showAddTagModal() {
            this.showCreateTagModal = true
        },

        // 快速添加标签 (用于下拉菜单)
        showQuickAddTag() {
            this.showAddTagModal()
        },

        // 过滤标签选项
        filterTagOption(input, option) {
            return option.props.children.toLowerCase().includes(input.toLowerCase())
        },

        // 处理分类创建成功
        handleCategoryCreated(newCategory) {
            this.categories.push(newCategory)
            this.articleData.category_id = newCategory.id
            this.showCreateCategoryModal = false
        },

        // 处理标签创建成功
        handleTagCreated(newTag) {
            this.tags.push(newTag)
            this.articleData.tag_ids.push(newTag.id)
            this.updateSelectedTags()
            this.showCreateTagModal = false
        },

        // 取消编辑
        cancelEdit() {
            this.isCancelling = true

            // 清理缓存（取消编辑时清理）
            this.clearDraftCache()

            // 模拟取消操作的延迟
            setTimeout(() => {
                message.success('已取消编辑')
                this.$router.push('/admin/dashboard')
                this.isCancelling = false
            }, 300)
        },

        // 处理草稿恢复
        async handleRecoverDraft(draft) {
            console.log('恢复草稿数据:', draft)

            // 先关闭模态框
            this.showRecoveryModal = false

            // 等待DOM更新
            await this.$nextTick()

            try {
                // 恢复文章数据到当前编辑器
                this.articleData.title = draft.title || ''
                this.articleData.excerpt = draft.excerpt || ''
                this.articleData.cover_image_url = draft.cover_image_url || ''
                this.articleData.category_id = draft.category_id || null
                this.articleData.tag_ids = Array.isArray(draft.tag_ids) ? draft.tag_ids : []
                this.articleData.isPublic = draft.isPublic !== undefined ? draft.isPublic : true
                this.articleData.article_type = draft.article_type || 'markdown'
                this.articleData.content_url = draft.content_url || ''

                // 恢复编辑器内容
                if (draft.content) {
                    if (this.articleData.article_type === 'richtext') {
                        this.richTextContent = draft.content
                        console.log('🔄 准备恢复富文本内容:', draft.content.substring(0, 200))

                        // 多次尝试设置富文本编辑器内容
                        const trySetContent = (attempt = 1) => {
                            if (this.$refs.richTextEditorRef && this.$refs.richTextEditorRef.setContent) {
                                try {
                                    console.log(`🔄 尝试设置富文本内容 (第${attempt}次) - Lake格式`)
                                    this.$refs.richTextEditorRef.setContent(draft.content, 'text/lake')
                                    console.log('✅ 富文本内容设置成功')
                                } catch (error) {
                                    console.error(`❌ 第${attempt}次设置富文本内容失败:`, error)
                                    if (attempt < 3) {
                                        setTimeout(() => trySetContent(attempt + 1), 500)
                                    }
                                }
                            } else {
                                console.log(`⏳ 富文本编辑器未就绪 (第${attempt}次)，等待重试...`)
                                if (attempt < 5) {
                                    setTimeout(() => trySetContent(attempt + 1), 300)
                                }
                            }
                        }

                        // 立即尝试一次，然后延迟尝试
                        await this.$nextTick()
                        trySetContent()

                    } else {
                        this.markdownContent = draft.content
                    }
                }

                // 更新选中的标签显示
                this.updateSelectedTags()

                message.success('草稿已恢复！')

            } catch (error) {
                console.error('恢复草稿失败:', error)
                message.error('恢复草稿失败：' + error.message)
            } finally {
                // 最后清理数据
                this.recoveryData = null
            }
        },

        // 忽略草稿
        async handleIgnoreDraft() {
            // 先关闭模态框
            this.showRecoveryModal = false

            // 等待DOM更新
            await this.$nextTick()

            // 清理被忽略的缓存
            this.clearDraftCache()

            message.info('已忽略草稿。')

            // 最后清理数据
            this.recoveryData = null
        },

        // 启动自动保存
        startAutoSave() {
            const draftStore = useArticleDraftStore()

            draftStore.startAutoSave(async () => {
                try {
                    const data = await this.getArticleDataForCache()
                    console.log('自动保存获取到的数据:', data)
                    return data
                } catch (error) {
                    console.error('获取缓存数据失败:', error)
                    return null
                }
            })
        },

        // 获取用于缓存的文章数据
        async getArticleDataForCache() {
            let currentContent = ''

            // 根据编辑器类型获取内容
            if (this.articleData.article_type === 'richtext') {
                // 优先从编辑器直接获取Lake格式内容（保持原始格式）
                if (this.$refs.richTextEditorRef && this.$refs.richTextEditorRef.getContent) {
                    try {
                        currentContent = this.$refs.richTextEditorRef.getContent('lake') || this.richTextContent || ''
                    } catch (error) {
                        console.warn('从富文本编辑器获取Lake内容失败，使用存储的内容:', error)
                        currentContent = this.richTextContent || ''
                    }
                } else {
                    currentContent = this.richTextContent || ''
                }
            } else {
                currentContent = this.markdownContent || ''
            }

            const cacheData = {
                title: this.articleData.title,
                excerpt: this.articleData.excerpt,
                content: currentContent,
                cover_image_url: this.articleData.cover_image_url,
                category_id: this.articleData.category_id,
                tag_ids: this.articleData.tag_ids,
                isPublic: this.articleData.isPublic,
                article_type: this.articleData.article_type,
                content_url: this.articleData.content_url
            }

            console.log('准备缓存的数据:', {
                title: cacheData.title,
                contentLength: cacheData.content?.length || 0,
                hasTitle: !!cacheData.title?.trim(),
                hasContent: !!cacheData.content?.trim()
            })

            return cacheData
        },

        // 从URL加载内容
        async loadContentFromUrl(url) {
            try {
                console.log('🔄 从对象存储URL加载内容:', url)
                const response = await fetch(url)
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                }
                const content = await response.text()

                // 根据文章类型设置对应编辑器的内容
                if (this.articleData.article_type === 'richtext') {
                    this.richTextContent = content
                    this.markdownContent = ''
                } else {
                    this.markdownContent = content
                    this.richTextContent = ''
                }

                console.log('✅ 内容加载成功，长度:', content.length)
            } catch (error) {
                console.error('❌ 从URL加载内容失败:', error)
                message.error('加载文章内容失败：' + error.message)
            }
        },

        // 处理存储模式切换
        async handleStorageModeChange(e) {
            const newMode = e.target.value
            const oldMode = this.storageMode

            if (newMode === oldMode) return

            console.log('🔄 存储模式切换:', oldMode, '->', newMode)

            try {
                if (newMode === 'object_storage') {
                    // 切换到对象存储：需要上传内容并清空数据库content
                    const content = this.getCurrentContent()
                    if (!content.trim()) {
                        message.warning('当前文章内容为空，无法切换到对象存储模式')
                        this.storageMode = oldMode
                        return
                    }

                    // 创建文件并上传
                    const fileName = `article-${this.id || 'new'}-${Date.now()}.${this.articleData.article_type === 'richtext' ? 'html' : 'md'}`
                    const file = new File([content], fileName, {
                        type: this.articleData.article_type === 'richtext' ? 'text/html' : 'text/markdown'
                    })

                    const result = await fileUploadService.uploadFile(file, 'articleFiles')
                    this.articleData.content_url = result.url

                    message.success('内容已上传到对象存储')

                } else {
                    // 切换到数据库存储：保持当前编辑器内容，content_url保留但不再使用
                    message.success('已切换到数据库存储模式')
                }

                this.storageMode = newMode

            } catch (error) {
                console.error('❌ 存储模式切换失败:', error)
                message.error('存储模式切换失败：' + error.message)
                this.storageMode = oldMode // 恢复原模式
            }
        },

        // 获取当前编辑器内容
        getCurrentContent() {
            if (this.articleData.article_type === 'richtext') {
                // 尝试从编辑器直接获取内容，如果失败则使用存储的内容
                try {
                    if (this.$refs.richTextEditorRef && this.$refs.richTextEditorRef.getContent) {
                        const editorContent = this.$refs.richTextEditorRef.getContent()
                        if (editorContent) {
                            return editorContent
                        }
                    }
                } catch (error) {
                    console.warn('无法从富文本编辑器获取内容，使用存储的内容:', error)
                }
                return this.richTextContent || ''
            } else {
                return this.markdownContent || ''
            }
        },

        // 清理缓存
        clearDraftCache() {
            const draftStore = useArticleDraftStore()
            draftStore.clearCurrentDraft()
        }
    }
}
</script>

<style scoped>
.article-editor {
    background: #f5f5f5;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    /* 移除固定高度和overflow限制，允许内容超出视口时滚动 */
}

.editor-page-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    background: white;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
    padding-bottom: 80px;
    /* 为底部固定按钮留出空间 */
}

/* 主要编辑区域 */
.main-editor-container {
    background: white;
    display: flex;
    flex-direction: column;
    height: 800px !important;
    /* 固定高度，不使用flex */
    overflow: hidden;
    flex-shrink: 0;
    /* 防止被压缩 */
}

.editor-wrapper {
    flex: 1;
    width: 100%;
    background: white;
    display: flex;
    flex-direction: column;
}

.editor-wrapper .richtext-editor {
    flex: 1;
    border: none;
    border-radius: 0;
    height: 100%;
}

/* Markdown编辑器样式调整 */
.editor-wrapper :deep(.md-editor) {
    flex: 1;
    border: none;
    border-radius: 0;
    box-shadow: none;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.editor-wrapper :deep(.md-editor-content) {
    flex: 1;
    overflow: hidden;
}

.editor-wrapper :deep(.md-editor-content-editor),
.editor-wrapper :deep(.md-editor-content-preview) {
    height: 100%;
    overflow-y: auto;
}

.editor-wrapper :deep(.md-editor-toolbar) {
    border-bottom: 1px solid #e8e8e8;
    background: #fafafa;
    padding: 12px 16px;
    flex-shrink: 0;
}

/* 文章设置区域 */
.article-settings {
    background: #f5f5f5;
    padding: 16px 24px;
    /* 减少垂直内边距 */
    border-top: 1px solid #e8e8e8;
    flex-shrink: 0;
    /* 防止收缩 */
}

.article-settings .ant-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border-radius: 8px;
}

.article-settings .ant-card-head {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px 8px 0 0;
}

.article-settings .ant-card-head-title {
    color: white;
    font-weight: 600;
    font-size: 16px;
}

.field-hint {
    margin-top: 8px;
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.4;
}

.cover-preview {
    margin-top: 12px;
    border-radius: 6px;
    overflow: hidden;
    max-width: 200px;
    border: 1px solid #e8e8e8;
}

.cover-preview img {
    width: 100%;
    height: auto;
}

.selected-tags {
    margin-bottom: 12px;
}

.selected-tags .ant-tag {
    margin-bottom: 4px;
}

.tags-section .ant-select {
    margin-bottom: 8px;
}

/* 新的表单布局样式 */
.settings-form {
    padding: 8px;
}

.form-item {
    margin-bottom: 20px;
}

.form-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
    font-size: 14px;
}

.form-label.required::after {
    content: '*';
    color: #ff4d4f;
    margin-left: 4px;
}

.article-settings .ant-input,
.article-settings .ant-select-selector,
.article-settings .ant-textarea {
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
}

.article-settings .ant-input:hover,
.article-settings .ant-select-selector:hover,
.article-settings .ant-textarea:hover {
    border-color: #40a9ff;
}

.article-settings .ant-input:focus,
.article-settings .ant-select-focused .ant-select-selector,
.article-settings .ant-textarea:focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
}

.article-settings .ant-switch {
    background: #bfbfbf;
}

.article-settings .ant-switch-checked {
    background: #52c41a;
}

/* 底部操作按钮样式 */
.bottom-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    border-top: 1px solid #e8e8e8;
    padding: 12px 0;
    /* 减少内边距 */
    z-index: 1000;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    /* 防止收缩 */
}

.action-buttons {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
}

.back-button {
    border: 1px solid #d9d9d9;
    background: white;
    color: #666;
    display: flex;
    align-items: center;
    gap: 6px;
    border-radius: 6px;
    transition: all 0.3s ease;
}

.back-button:hover {
    border-color: #40a9ff;
    color: #40a9ff;
}

.right-buttons .ant-select {
    border-radius: 6px;
}

.right-buttons .ant-select .ant-select-selector {
    border-radius: 6px;
}

.bottom-actions .ant-btn {
    min-width: 120px;
    height: 40px;
    font-weight: 500;
    border-radius: 6px;
}

.bottom-actions .ant-btn-primary {
    background: #1890ff;
    border-color: #1890ff;
    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3);
}

.bottom-actions .ant-btn-primary:hover {
    background: #40a9ff;
    border-color: #40a9ff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(24, 144, 255, 0.4);
}

.bottom-actions .ant-btn:not(.ant-btn-primary) {
    border-color: #d9d9d9;
    color: #666;
}

.bottom-actions .ant-btn:not(.ant-btn-primary):hover {
    border-color: #40a9ff;
    color: #40a9ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 响应式设计 */
@media (max-width: 1240px) {
    .editor-page-content {
        margin: 0 20px;
        max-width: calc(100% - 40px);
    }

    .action-buttons {
        max-width: calc(100% - 40px);
        padding: 0 20px;
    }
}

@media (max-width: 768px) {
    .editor-page-content {
        margin: 0;
        max-width: 100%;
        padding-bottom: 120px;
        /* 移动端底部按钮需要更多空间 */
    }

    .main-editor-container {
        height: 800px !important;
        /* 保持和桌面端一样的高度 */
        flex-shrink: 0;
        /* 防止被压缩 */
    }

    .article-settings {
        padding: 12px 16px;
        /* 移动端进一步减少内边距 */
    }

    .article-settings .ant-card-head-title {
        font-size: 14px;
    }
}

/* 使用默认主题色 */
</style>